import React from 'react';
import { Link } from 'react-router-dom';

const BabyTrackPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            BabyTrack - Love your Baby by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Effective date: 14 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Wolf Technologies Ltd ("we", "us", or "our") operates the BabyTrack - Love your Baby
              mobile application (the "App"). This Privacy Policy explains how we collect, use, store,
              and protect your personal information and your baby's data when you use our App.
            </p>
            <p>
              By using BabyTrack, you agree to the collection and use of information in accordance
              with this policy. If you do not agree with this policy, please do not use the App.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>We collect the following types of data to provide and improve the App:</p>

            <h3>Account Information</h3>
            <ul>
              <li>Email address (for account login and authentication)</li>
            </ul>

            <h3>Baby Profiles</h3>
            <ul>
              <li>Baby's name</li>
              <li>Date of birth</li>
              <li>Gender</li>
            </ul>

            <h3>Health Data</h3>
            <ul>
              <li>Growth measurements (height, weight, head circumference)</li>
              <li>Temperature readings</li>
              <li>Illness symptoms</li>
              <li>Medications</li>
              <li>Allergies</li>
            </ul>

            <h3>Usage & Activity Data</h3>
            <ul>
              <li>Feeding logs (breast, bottle, solids)</li>
              <li>Sleep logs</li>
              <li>Nappy change records</li>
              <li>Activity logs</li>
            </ul>

            <h3>Other Data</h3>
            <ul>
              <li>Mood tracker entries</li>
              <li>Caregiver invitations (email addresses of invited caregivers)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be transparent about what we do not collect:</p>
            <ul>
              <li>
                <strong>Location data</strong> — The UV Index feature uses client-side geolocation only.
                Your location is never sent to our servers.
              </li>
              <li><strong>Photos</strong></li>
              <li><strong>Browsing history</strong></li>
              <li><strong>Contacts</strong></li>
              <li>
                <strong>Financial information</strong> — All payments are handled directly by Apple
                (In-App Purchase) or Stripe. We do not store your payment card details.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use the data we collect to:</p>
            <ul>
              <li>Provide and maintain the App's core tracking features</li>
              <li>Authenticate your account and manage login sessions</li>
              <li>Enable sharing of baby data with caregivers you invite</li>
              <li>Generate growth charts and insights (for eligible subscription tiers)</li>
              <li>Improve the App and develop new features</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>
              All data is stored securely on AWS infrastructure in the EU, specifically in the
              London region (eu-west-2). Data is encrypted both in transit (TLS) and at rest.
            </p>
            <p>
              We implement industry-standard security measures to protect your data, including
              secure authentication via AWS Cognito and encrypted database storage via AWS DynamoDB.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate the App:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — User authentication and account management</li>
              <li><strong>AWS DynamoDB</strong> — Secure data storage</li>
              <li><strong>Stripe</strong> — Web payment processing</li>
              <li><strong>Apple In-App Purchase</strong> — iOS payment processing</li>
            </ul>
            <p>
              We do not sell or share your data with advertisers. We do not use any third-party
              analytics SDKs. Your data is only shared with the services listed above as necessary
              to provide the App's functionality.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              Your baby's data is private by default. You may optionally choose to share data by:
            </p>
            <ul>
              <li>
                <strong>Inviting caregivers</strong> — You can invite other users (by email) to
                access your baby's data. Only people you explicitly invite will have access.
              </li>
              <li>
                <strong>Temporary access links</strong> — You can create temporary links to share
                baby data. These links provide time-limited access to the people you choose to
                share them with.
              </li>
            </ul>
            <p>
              Data is only ever shared with people you explicitly invite or grant access to.
              We never share your data with third parties for marketing or advertising purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <p>
              Your data is retained for as long as your account is active. You have full control
              over your data:
            </p>
            <ul>
              <li>
                <strong>Delete individual babies</strong> — Deleting a baby profile will cascade
                delete all associated records (feeds, sleeps, nappies, growth data, etc.).
              </li>
              <li>
                <strong>Delete your account</strong> — You can request complete account deletion,
                which will remove all your data from our systems.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Children's Privacy</h2>
            <p>
              BabyTrack is designed for parents and caregivers, not for children to use directly.
              All baby data is entered and managed by the parent or caregiver. The App does not
              knowingly collect personal information directly from children.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Your Rights</h2>
            <p>Under applicable data protection laws, you have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Request a portable copy of your data</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at the email address below.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy within the App and updating the
              "Effective date" at the top of this page.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or your data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default BabyTrackPrivacy;
